import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useChallenge } from './useChallenge';
import GameHUD from '../shared/GameHUD';
import PlayerAvatar from '../shared/PlayerAvatar';
import { Check } from 'lucide-react';

/**
 * Challenge drawing phase — each player draws on their own private canvas.
 * Enters solo mode so strokes never hit RTDB.
 */
export default function ChallengeDrawing({ stageRef }) {
  const { state, dispatch } = useApp();
  const { game, user, room, canvas } = state;
  const { submitEntry, advanceToReview } = useChallenge();
  const [submitted, setSubmitted] = useState(false);
  const initialisedRef = useRef(false);
  const advancedRef = useRef(false);

  // On mount: enter solo mode for private drawing
  // Canvas has already been cleared by handleStartGame at game start,
  // and savedSharedStrokes is already populated for later restore.
  // We only need to enter solo mode here so strokes stay local.
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    if (!canvas.soloMode) {
      dispatch({ type: 'TOGGLE_SOLO_MODE' });
    }

    // Apply challenge style preset (if the host picked one)
    const challengeStyle = game.challenge?.style;
    if (challengeStyle && challengeStyle.toolPreset) {
      const { tool, size, opacity, palette } = challengeStyle.toolPreset;
      if (tool) dispatch({ type: 'SET_TOOL', payload: tool });
      if (typeof size === 'number') dispatch({ type: 'SET_SIZE', payload: size });
      if (typeof opacity === 'number') dispatch({ type: 'SET_OPACITY', payload: opacity });
      if (palette && palette.length > 0) {
        dispatch({ type: 'SET_COLOUR', payload: palette[0] });
      }
      dispatch({
        type: 'SET_ACTIVE_STYLE',
        payload: {
          id: challengeStyle.id,
          name: challengeStyle.name,
          emoji: challengeStyle.emoji,
          colour: challengeStyle.colour,
          palette: palette || [],
        },
      });
    }
  }, [dispatch, canvas.soloMode, game.challenge]);

  // On unmount: clear the active style
  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_ACTIVE_STYLE' });
    };
  }, [dispatch]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (submitted) return;
    setSubmitted(true);
    await submitEntry(stageRef);
  }, [submitted, submitEntry, stageRef]);

  // Host: when timer expires, advance to review
  const handleTimerExpire = useCallback(async () => {
    // Auto-submit if not already done
    if (!submitted) {
      setSubmitted(true);
      await submitEntry(stageRef);
    }
    // Only host advances the phase (with guard against double-fire)
    if (user.isHost && !advancedRef.current) {
      advancedRef.current = true;
      setTimeout(() => advanceToReview(), 1500);
    }
  }, [submitted, submitEntry, stageRef, user.isHost, advanceToReview]);

  // Check who has submitted
  const submissions = game.submissions || {};
  const members = Object.entries(room.members || {});
  const submittedCount = Object.keys(submissions).length;

  // If all submitted early (before timer), host advances
  useEffect(() => {
    if (user.isHost && !advancedRef.current && submittedCount > 0 && submittedCount >= members.length) {
      advancedRef.current = true;
      setTimeout(() => advanceToReview(), 1000);
    }
  }, [user.isHost, submittedCount, members.length, advanceToReview]);

  const challengeStyle = game.challenge?.style;
  const promptLabel = challengeStyle
    ? `${game.challenge?.text} — in ${challengeStyle.name}`
    : (game.challenge?.text || 'Draw!');

  return (
    <GameHUD
      timerEndsAt={game.timerEndsAt}
      timerDuration={game.timerDuration}
      onTimerExpire={handleTimerExpire}
      emoji={challengeStyle ? challengeStyle.emoji : '🎨'}
      label={promptLabel}
      sublabel={submitted ? 'Submitted!' : challengeStyle ? `The ${challengeStyle.name} way` : 'Draw your best version'}
    >
      {/* Bottom bar: submit button or waiting status */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-100 flex items-center gap-4">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
          >
            <Check className="w-4 h-4" />
            Submit Early
          </button>
        ) : (
          <p className="text-sm text-slate-500 font-display">Waiting for others...</p>
        )}

        {/* Submission tracker */}
        <div className="flex items-center gap-2">
          {members.map(([id, member]) => (
            <div
              key={id}
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                submissions[id]
                  ? 'border-green-400 bg-green-50'
                  : 'border-slate-200 bg-white'
              }`}
              title={member.nickname}
            >
              {submissions[id] ? (
                <Check className="w-3 h-3 text-green-500" />
              ) : (
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: member.colour }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </GameHUD>
  );
}
